import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Instagram, 
  Twitter, 
  Youtube, 
  Link2, 
  Unlink, 
  RefreshCw,
  Shield,
  Search,
  AlertTriangle,
  CheckCircle,
  XCircle,
  History,
  ChevronRight,
  Filter
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL as API } from '../config/api';
import { safeJson } from '../utils/safeJson';



// TikTok icon
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const PlatformIcon = ({ platform, className }) => {
  const icons = { instagram: Instagram, twitter: Twitter, youtube: Youtube, tiktok: TikTokIcon };
  const Icon = icons[platform] || Link2;
  return <Icon className={className} />;
};

export default function AdminSocialPage() {
  const { user, token } = useAuth();
  const [connections, setConnections] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', platform: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showLogs, setShowLogs] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokingId, setRevokingId] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  const fetchData = useCallback(async () => {
    if (!token || !isAdmin) return;
    
    try {
      setLoading(true);
      
      // Fetch connections
      let url = `${API}/api/admin/social/connections?limit=100`;
      if (filters.status) url += `&status=${filters.status}`;
      if (filters.platform) url += `&platform=${filters.platform}`;
      
      const connRes = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const connData = await connRes.json();
      setConnections(connData.connections || []);
      
      // Fetch sync logs
      const logsRes = await fetch(`${API}/api/admin/social/sync-logs?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const logsData = await logsRes.json();
      setSyncLogs(logsData.logs || []);
      
    } catch (error) {
      console.error('Error fetching admin social data:', error);
      toast.error('Failed to load social connections');
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRevoke = async (accountId) => {
    if (!revokeReason.trim()) {
      toast.error('Please provide a reason for revocation');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/admin/social/revoke/${accountId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: revokeReason })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to revoke');
      }
      
      toast.success('Connection revoked successfully');
      setRevokingId(null);
      setRevokeReason('');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Filter connections by search term
  const filteredConnections = connections.filter(conn => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      conn.user_id?.toLowerCase().includes(search) ||
      conn.external_username?.toLowerCase().includes(search) ||
      conn.platform?.toLowerCase().includes(search)
    );
  });

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-bold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">Admin privileges required</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" data-testid="admin-social-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-red-500/20 via-orange-500/10 to-yellow-500/10 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Admin: Social Connections</h1>
              <p className="text-muted-foreground">Manage user social media integrations</p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex flex-wrap gap-4 mt-6">
            <Badge variant="outline" className="text-lg py-1 px-3">
              {connections.length} Total Connections
            </Badge>
            <Badge variant="outline" className="text-lg py-1 px-3 text-green-500 border-green-500/30">
              {connections.filter(c => c.status === 'connected').length} Active
            </Badge>
            <Badge variant="outline" className="text-lg py-1 px-3 text-yellow-500 border-yellow-500/30">
              {connections.filter(c => c.status === 'disconnected').length} Disconnected
            </Badge>
            <Badge variant="outline" className="text-lg py-1 px-3 text-red-500 border-red-500/30">
              {connections.filter(c => c.status === 'revoked').length} Revoked
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Status</SelectItem>
                <SelectItem value="connected">Connected</SelectItem>
                <SelectItem value="disconnected">Disconnected</SelectItem>
                <SelectItem value="revoked">Revoked</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.platform} onValueChange={(v) => setFilters(f => ({ ...f, platform: v }))}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Platforms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Platforms</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="twitter">Twitter/X</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="youtube">YouTube</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by user ID or username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>
        </Card>

        {/* Connections List */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Connections ({filteredConnections.length})</h2>
          
          <div className="space-y-3">
            {filteredConnections.map((conn) => (
              <Card key={conn.account_id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      conn.platform === 'instagram' ? 'bg-gradient-to-br from-purple-500 to-pink-500' :
                      conn.platform === 'youtube' ? 'bg-red-600' : 'bg-black'
                    }`}>
                      <PlatformIcon platform={conn.platform} className="w-5 h-5 text-white" />
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium capitalize">{conn.platform}</span>
                        <Badge variant={
                          conn.status === 'connected' ? 'outline' :
                          conn.status === 'revoked' ? 'destructive' : 'secondary'
                        } className={
                          conn.status === 'connected' ? 'text-green-500 border-green-500/30' : ''
                        }>
                          {conn.status}
                        </Badge>
                        {conn.mock_mode && (
                          <Badge variant="secondary" className="text-xs">Mock</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        User: {conn.user_id?.substring(0, 20)}...
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {conn.external_username} • Tier: {conn.tier_at_connection || 'N/A'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {new Date(conn.created_at).toLocaleDateString()}
                    </span>
                    
                    {conn.status === 'connected' && (
                      revokingId === conn.account_id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="Reason for revocation"
                            value={revokeReason}
                            onChange={(e) => setRevokeReason(e.target.value)}
                            className="w-48 h-8 text-sm"
                          />
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleRevoke(conn.account_id)}
                          >
                            Confirm
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => { setRevokingId(null); setRevokeReason(''); }}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-500 hover:text-red-600"
                          onClick={() => setRevokingId(conn.account_id)}
                        >
                          <Unlink className="w-4 h-4 mr-1" />
                          Revoke
                        </Button>
                      )
                    )}
                    
                    {conn.status === 'revoked' && conn.revoke_reason && (
                      <span className="text-xs text-red-400">
                        Reason: {conn.revoke_reason}
                      </span>
                    )}
                  </div>
                </div>
              </Card>
            ))}
            
            {filteredConnections.length === 0 && (
              <Card className="p-8 text-center">
                <Link2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No connections found</p>
              </Card>
            )}
          </div>
        </section>

        {/* Sync Logs */}
        <section>
          <button 
            onClick={() => setShowLogs(!showLogs)}
            className="flex items-center gap-2 text-xl font-semibold mb-4 hover:text-primary transition-colors"
          >
            <History className="w-5 h-5" />
            Sync Activity Logs
            <ChevronRight className={`w-5 h-5 transition-transform ${showLogs ? 'rotate-90' : ''}`} />
          </button>
          
          <AnimatePresence>
            {showLogs && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                <Card className="p-4">
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {syncLogs.map((log, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-muted last:border-0">
                        <div className="flex items-center gap-3">
                          <PlatformIcon platform={log.platform} className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm">
                              <span className="font-medium capitalize">{log.action.replace('_', ' ')}</span>
                              <span className="text-muted-foreground"> by {log.user_id?.substring(0, 12)}...</span>
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(log.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <Badge variant={log.status === 'success' ? 'outline' : 'destructive'} className={
                          log.status === 'success' ? 'text-green-500 border-green-500/30' : ''
                        }>
                          {log.status === 'success' ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                          {log.status}
                        </Badge>
                      </div>
                    ))}
                    
                    {syncLogs.length === 0 && (
                      <p className="text-center text-muted-foreground py-4">No activity logs</p>
                    )}
                  </div>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
      </div>
    </div>
  );
}
